import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import bindingRegistry from './BindingRegistry.js';
import actionExecutor from './ActionExecutor.js';
import { buildKeyString, shouldIgnoreKeyEvent } from './utils.js';

// Keybinding state management
const initialState = {
  currentSequence: [],
  isBuilding: false,
  lastAction: null,
  history: [],
  enabled: true,
  visualizationEvents: [],
};

// Action types
const ACTIONS = {
  ADD_TO_SEQUENCE: 'ADD_TO_SEQUENCE',
  RESET_SEQUENCE: 'RESET_SEQUENCE', 
  SET_ENABLED: 'SET_ENABLED',
  RECORD_ACTION: 'RECORD_ACTION',
  ADD_VISUALIZATION_EVENT: 'ADD_VISUALIZATION_EVENT',
  REMOVE_VISUALIZATION_EVENT: 'REMOVE_VISUALIZATION_EVENT',
};

// Reducer for keybinding state
const keybindingReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.ADD_TO_SEQUENCE:
      return {
        ...state,
        currentSequence: [...state.currentSequence, action.key],
        isBuilding: true,
      };

    case ACTIONS.RESET_SEQUENCE:
      return {
        ...state,
        currentSequence: [],
        isBuilding: false,
      };

    case ACTIONS.SET_ENABLED:
      return {
        ...state,
        enabled: action.enabled,
      };

    case ACTIONS.RECORD_ACTION:
      const newHistory = [
        ...state.history.slice(-49), // Keep last 50 entries
        {
          sequence: action.sequence,
          actionName: action.actionName,
          timestamp: Date.now(),
          success: action.success,
        }
      ];
      
      return {
        ...state,
        lastAction: action.actionName,
        history: newHistory,
      };

    case ACTIONS.ADD_VISUALIZATION_EVENT:
      return {
        ...state,
        visualizationEvents: [...state.visualizationEvents, action.event],
      };

    case ACTIONS.REMOVE_VISUALIZATION_EVENT:
      return {
        ...state,
        visualizationEvents: state.visualizationEvents.filter(e => e.id !== action.eventId),
      };

    default:
      return state;
  }
};

// Helper function to create visualization events
const createVisualizationEvent = (type, sequence, description = '', isMultiKey = false) => ({
  id: `${type}-${sequence.join('-')}-${Date.now()}`,
  type, // 'key-press', 'action-executed', 'sequence-cancelled'
  sequence: [...sequence], // copy to avoid mutations
  description,
  isMultiKey,
  timestamp: Date.now(),
});

// Create context
const KeybindingContext = createContext(null);

/**
 * KeybindingProvider - Main provider for the keybinding system
 */
export const KeybindingProvider = ({ children }) => {
  const [state, dispatch] = useReducer(keybindingReducer, initialState);

  // Main key event handler
  const handleKeyEvent = useCallback((event) => {
    // Skip if keybindings are disabled
    if (!state.enabled) return;
    
    // Early exit for input elements to reduce processing
    const target = event.target;
    
    // Check for explicit keybinding-ignore attribute
    if (target && target.hasAttribute && target.hasAttribute('data-keybinding-ignore')) {
      return;
    }
    
    if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) {
      // In inputs, allow Escape (to reset sequences) and modifier-based shortcuts
      // Let individual binding `when` clauses handle finer control
      if (!event.metaKey && !event.ctrlKey && event.key !== 'Escape') {
        return;
      }
    }
    
    // Skip if this event should be ignored
    if (shouldIgnoreKeyEvent(event)) return;
    
    const key = buildKeyString(event);

    // Debug logging to see what keys are being generated (only when building or for potential shortcuts)
    if (import.meta.env.DEV && (state.isBuilding || key.length <= 3)) {
      console.log(`[Keybinding Debug] Key pressed:`, {
        key: event.key,
        code: event.code,
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        altKey: event.altKey,
        metaKey: event.metaKey,
        generatedKey: key
      });
    }
    
    // ESCAPE always resets sequences
    if (key === 'Escape') {
      if (state.isBuilding) {
        event.preventDefault();
        
        // Create visualization event for cancelled sequence
        const visualEvent = createVisualizationEvent(
          'sequence-cancelled', 
          state.currentSequence, 
          'Cancelled', 
          true
        );
        dispatch({ type: ACTIONS.ADD_VISUALIZATION_EVENT, event: visualEvent });
        
        dispatch({ type: ACTIONS.RESET_SEQUENCE });

        if (import.meta.env.DEV) {
          console.log('[Keybinding] Sequence reset by Escape');
        }
        return;
      }
      // Let Escape bubble up if not building sequence
    }
    
    // Skip registry lookup for lone alphanumeric keys when not building (optimization)
    if (!state.isBuilding && /^[a-zA-Z0-9]$/.test(key)) {
      // Check if it's a registered single key binding OR could start a sequence
      const singleKeyMatch = bindingRegistry.findExactMatch(key);
      const couldStartSequence = bindingRegistry.hasPartialMatch(key);
      
      if (!singleKeyMatch && !couldStartSequence) {
        return; // Safe to skip - not a keybinding at all
      }
    }
    
    // Build potential sequence
    const potentialSequence = [...state.currentSequence, key];
    const sequenceStr = potentialSequence.join(' ');
    
    // Check for exact match first
    const exactMatch = bindingRegistry.findExactMatch(sequenceStr);
    if (exactMatch) {
      event.preventDefault();
      
      // Create visualization event for executed action
      const visualEvent = createVisualizationEvent(
        'action-executed', 
        potentialSequence, 
        exactMatch.description || '', 
        potentialSequence.length > 1
      );
      dispatch({ type: ACTIONS.ADD_VISUALIZATION_EVENT, event: visualEvent });
      
      executeAction(exactMatch.action, sequenceStr, exactMatch);
      dispatch({ type: ACTIONS.RESET_SEQUENCE });
      return;
    }
    
    // Check if this could be start of a longer sequence
    const partialMatch = bindingRegistry.hasPartialMatch(sequenceStr);
    if (partialMatch) {
      event.preventDefault();
      
      // Create visualization event for multi-key sequence building
      const visualEvent = createVisualizationEvent(
        'sequence-building', 
        potentialSequence, 
        'Building sequence...', 
        true
      );
      dispatch({ type: ACTIONS.ADD_VISUALIZATION_EVENT, event: visualEvent });
      
      dispatch({
        type: ACTIONS.ADD_TO_SEQUENCE,
        key
      });

      if (import.meta.env.DEV) {
        console.log(`[Keybinding] Building sequence: ${sequenceStr}`);
      }
      return;
    }
    
    // No match - handle gracefully
    if (state.isBuilding) {
      // We were building a sequence, but this key doesn't continue it
      dispatch({ type: ACTIONS.RESET_SEQUENCE });
      
      // Try the new key as a fresh sequence
      const freshMatch = bindingRegistry.findExactMatch(key);
      if (freshMatch) {
        event.preventDefault();
        executeAction(freshMatch.action, key, freshMatch);
        return;
      }
      
      const freshPartial = bindingRegistry.hasPartialMatch(key);
      if (freshPartial) {
        event.preventDefault();
        dispatch({ 
          type: ACTIONS.ADD_TO_SEQUENCE,
          key 
        });
        return;
      }

      // Key doesn't match anything - let it through for default behavior
      if (import.meta.env.DEV) {
        console.log(`[Keybinding] No binding for "${key}", allowing default behavior`);
      }
    }

    // If we're not building and key doesn't match, let it bubble
    // This preserves normal browser/app functionality
  }, [state.enabled, state.isBuilding, state.currentSequence]);

  // Execute an action
  const executeAction = useCallback(async (actionName, sequence, binding) => {
    try {
      // Check if the action has a 'when' condition and if it's currently disabled
      if (binding.when && typeof binding.when === 'function') {
        const isEnabled = binding.when();
        if (!isEnabled) {
          // Show toast notification for disabled command
          const reason = binding.disabledReason || 'Command not available in current context';
          toast(`${binding.description || 'Command'} - ${reason}`);
          
          dispatch({
            type: ACTIONS.RECORD_ACTION,
            sequence,
            actionName: `${actionName} (disabled)`,
            success: false,
          });

          if (import.meta.env.DEV) {
            console.log(`[Keybinding] Disabled: ${sequence} → ${actionName} (${reason})`);
          }

          return;
        }
      }
      
      const context = {
        sequence,
        binding,
        timestamp: Date.now(),
      };
      
      const success = await actionExecutor.execute(actionName, context);
      
      dispatch({
        type: ACTIONS.RECORD_ACTION,
        sequence,
        actionName,
        success,
      });

      if (import.meta.env.DEV) {
        console.log(`[Keybinding] Executed: ${sequence} → ${actionName} (${success ? 'success' : 'failed'})`);
      }

    } catch (error) {
      console.error('[Keybinding] Action execution failed:', error);
      
      dispatch({
        type: ACTIONS.RECORD_ACTION,
        sequence,
        actionName,
        success: false,
      });
    }
  }, []);

  // Register global keyboard listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      handleKeyEvent(event);
    };
    
    // Use capture phase to intercept before other handlers
    document.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [handleKeyEvent]);

  // API for managing keybindings
  const api = {
    // State
    ...state,
    
    // Actions
    resetSequence: () => dispatch({ type: ACTIONS.RESET_SEQUENCE }),
    setEnabled: (enabled) => dispatch({ type: ACTIONS.SET_ENABLED, enabled }),
    removeVisualizationEvent: (eventId) => dispatch({ type: ACTIONS.REMOVE_VISUALIZATION_EVENT, eventId }),
    
    // Registry access
    register: bindingRegistry.register.bind(bindingRegistry),
    unregister: bindingRegistry.unregister.bind(bindingRegistry),
    getAllBindings: bindingRegistry.getAllBindings.bind(bindingRegistry),
    getStats: bindingRegistry.getStats.bind(bindingRegistry),
    
    // Execution
    executeAction,
    
    // Registry and executor access for advanced use
    registry: bindingRegistry,
    executor: actionExecutor,
  };

  return (
    <KeybindingContext.Provider value={api}>
      {children}
    </KeybindingContext.Provider>
  );
};

/**
 * Hook to access keybinding context
 */
export const useKeybindingContext = () => {
  const context = useContext(KeybindingContext);
  if (!context) {
    throw new Error('useKeybindingContext must be used within a KeybindingProvider');
  }
  return context;
};

/**
 * Global listener component - handles keyboard events at document level
 */
export const KeybindingListener = () => {
  // The actual listening is handled in the provider via useEffect
  // This component exists for semantic clarity in the component tree
  return null;
};

export default KeybindingContext;
